import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react'
import { map, chain } from 'lodash'
import {
  AmountUnit,
  DurationUnit,
  FrequencyUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
  amountUnits,
  durationUnits,
  frequencyUnits,
  methods,
  percentages,
} from '../../types/Regimen'
import BasicDateStringPicker from '../BasicDateStringPicker'
import useRegimenForm from '../../hooks/useRegimenForm'
import SelectField from './form-fields/SelectField'

interface RegimenListItem {
  label: string
  fieldId: string
  items: { [itemId: string]: string }
  id: string
}

interface RegimenFormModalProps {
  isOpen: boolean
  onClose: () => void
  lists: RegimenListItem[]
  editingId?: string
  editingUsageIndex?: number
  recordLabel: string
  records: Regimen[]
  reportingForEnd: string
  amountEnabled?: boolean
  frequencyEnabled?: boolean
  durationEnabled?: boolean
  methodEnabled?: boolean
  percentageEnabled?: boolean
  initialStartDate?: string
  initialFrequency?: string
  initialFrequencyUnit?: FrequencyUnit
  initialAmount?: string
  initialAmountUnit?: AmountUnit
  initialMethod?: Method
  initialSelectedFields?: SelectedFields
  initialDuration?: string
  initialDurationUnit?: DurationUnit
  initialPercentage?: Percentage
  reactivatingPrimaryId?: string
}

export default function RegimenFormModal({
  isOpen,
  onClose,
  lists,
  editingId,
  editingUsageIndex,
  recordLabel,
  records,
  reportingForEnd,
  amountEnabled,
  frequencyEnabled,
  durationEnabled,
  methodEnabled,
  percentageEnabled,
  initialStartDate,
  initialFrequency,
  initialFrequencyUnit,
  initialAmount,
  initialAmountUnit,
  initialMethod,
  initialSelectedFields,
  initialDuration,
  initialDurationUnit,
  initialPercentage,
  reactivatingPrimaryId,
}: RegimenFormModalProps) {
  const {
    selectedFields,
    setSelectedFields,
    startDate,
    setStartDate,
    frequency,
    setFrequency,
    amount,
    setAmount,
    duration,
    setDuration,
    method,
    setMethod,
    frequencyUnit,
    setFrequencyUnit,
    amountUnit,
    setAmountUnit,
    durationUnit,
    setDurationUnit,
    percentage,
    setPercentage,
    handleSubmit,
    handleClose,
    getBrandIfDuplicate,
  } = useRegimenForm({
    lists,
    editingId,
    editingUsageIndex,
    records,
    reportingForEnd,
    amountEnabled,
    frequencyEnabled,
    durationEnabled,
    methodEnabled,
    percentageEnabled,
    onClose,
    reactivatingPrimaryId,
    initialStartDate,
    initialFrequency,
    initialFrequencyUnit,
    initialAmount,
    initialAmountUnit,
    initialMethod,
    initialSelectedFields,
    initialDuration,
    initialDurationUnit,
    initialPercentage,
  })

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent as='form' onSubmit={handleSubmit}>
        <ModalHeader>
          {reactivatingPrimaryId ? 'Reactivate' : editingId ? 'Edit' : 'Add'}{' '}
          {recordLabel}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            {lists.map((list, index) => (
              <SelectField
                key={list.fieldId}
                list={list}
                selectedFields={selectedFields}
                setSelectedFields={setSelectedFields}
                index={index}
                editingId={editingId}
                getBrandIfDuplicate={getBrandIfDuplicate}
                onDuplicateCancel={handleClose}
              />
            ))}

            <FormControl isRequired>
              <FormLabel>
                {editingId
                  ? 'What date did you change?'
                  : lists[0].id === 'dentalProcedures'
                    ? 'Procedure Date'
                    : 'Start Date'}
              </FormLabel>
              <BasicDateStringPicker
                startYear={'1920'}
                endYear={new Date().getFullYear().toString()}
                dateString={startDate}
                setDateString={setStartDate}
              />
            </FormControl>

            {percentageEnabled && (
              <FormControl isRequired>
                <FormLabel>Percentage of Protocol followed</FormLabel>
                <RadioGroup
                  value={percentage}
                  onChange={(value) => setPercentage(value as Percentage)}
                >
                  <HStack spacing={2}>
                    {map(percentages, (label, percentageValue) => (
                      <Radio key={percentageValue} value={percentageValue}>
                        {label}
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}

            {amountEnabled && (
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Amount</FormLabel>
                  <Input
                    required
                    type='number'
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    step='any'
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Unit</FormLabel>
                  <Select
                    required
                    value={amountUnit || ''}
                    placeholder='Choose Unit'
                    onChange={(e) =>
                      setAmountUnit(e.target.value as AmountUnit)
                    }
                  >
                    {chain(amountUnits)
                      .map((title, value) => ({ title, value }))
                      .orderBy((item) => item.title.toLowerCase(), 'asc')
                      .map(({ title, value }) => (
                        <option value={value} key={value}>
                          {title}
                        </option>
                      ))
                      .value()}
                  </Select>
                </FormControl>
              </SimpleGrid>
            )}

            {durationEnabled && (
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Duration</FormLabel>
                  <Input
                    required
                    type='number'
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    step='any'
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Unit</FormLabel>
                  <Select
                    required
                    value={durationUnit || ''}
                    placeholder='Choose Unit'
                    onChange={(e) =>
                      setDurationUnit(e.target.value as DurationUnit)
                    }
                  >
                    {map(durationUnits, (title, value) => (
                      <option key={value} value={value}>
                        {title}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            )}

            {frequencyEnabled && (
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Frequency</FormLabel>
                  <InputGroup>
                    <Input
                      required
                      type='number'
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      step='any'
                    />
                    <InputRightAddon>x</InputRightAddon>
                  </InputGroup>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Per</FormLabel>
                  <Select
                    required
                    value={frequencyUnit || ''}
                    placeholder='Choose Unit'
                    onChange={(e) =>
                      setFrequencyUnit(e.target.value as FrequencyUnit)
                    }
                  >
                    {map(frequencyUnits, (title, value) => (
                      <option key={value} value={value}>
                        {title}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            )}

            {methodEnabled && (
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Method</FormLabel>
                  <Select
                    required
                    value={method}
                    placeholder='Choose Method'
                    onChange={(e) => setMethod(e.target.value as Method)}
                  >
                    {chain(methods)
                      .map((title, value) => ({ title, value }))
                      .orderBy('title', 'asc')
                      .map(({ title, value }) => (
                        <option key={value} value={value}>
                          {title}
                        </option>
                      ))
                      .value()}
                  </Select>
                </FormControl>
              </SimpleGrid>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button size='sm' colorScheme='gray' onClick={handleClose} mr={3}>
            Cancel
          </Button>
          <Button size='sm' type='submit'>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
